import { Stage as PixiStage } from "@pixi/react";
import { Point } from "@Shared/types/ScenarioTypes";
import { isEqual, isMap, isNumber, isString } from "lodash-es";
import { merge } from "merge-anything";
import React from "react";
import {
  BuilderState,
  FieldPosition,
  FieldSpace,
  Player,
  ScenarioClip,
  ScenarioState,
  StateChangeSource,
  TargetMode,
} from "../../../containers/game/builder/models/builder-data-types";

const teamAColor = "rgb(120, 160, 240)";
const teamBColor = "rgb(220, 80, 80)";
const pressureColor = "0xFFBB33";

export const fieldPositions: FieldPosition[] = [
  { name: "goalkeeper", initials: "GK", jerseyNumber: 1, synonyms: ["goalie", "keeper"] },
  { name: "left back", initials: "LB", jerseyNumber: 3, synonyms: ["left fullback", "left defender"] },
  { name: "left centre back", initials: "LCB", jerseyNumber: 5, synonyms: ["left centre defender", "left center defender", "left central defender"] },
  {
    name: "right centre back",
    initials: "RCB",
    jerseyNumber: 4,
    synonyms: ["right centre defender", "right center defender", "right central defender"],
  },
  { name: "right back", initials: "RB", jerseyNumber: 2, synonyms: ["right fullback", "right defender"] },
  { name: "defensive midfielder", initials: "DM", jerseyNumber: 6, synonyms: [] },
  { name: "central midfielder", initials: "CM", jerseyNumber: 8, synonyms: ["center midfielder", "centre midfielder"] },
  { name: "attacking midfielder", initials: "AM", jerseyNumber: 10, synonyms: [] },
  { name: "left winger", initials: "LW", jerseyNumber: 11, synonyms: ["left forward", "left wing"] },
  { name: "right winger", initials: "RW", jerseyNumber: 7, synonyms: ["right forward", "right wing"] },
  { name: "striker", initials: "ST", jerseyNumber: 9, synonyms: ["forward"] },
];

const kickHeights = [
  { height: 0.0, name: "Ground", min: 0, max: 0 },
  { height: 0.3, name: "Low", min: 0.1, max: 0.65 },
  { height: 1, name: "Medium", min: 0.65, max: 2.0 },
  { height: 3, name: "High", min: 2.0, max: 5.0 },
  { height: 7, name: "Very High", min: 5.0, max: Infinity },
];

const ids = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22];
const scores = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
const scoreTimes = [0.5, 1, 1.5, 2, 2.5, 3, 4, 5];
const largeScoreTimes = [10, 15, 20, 30, 45, 60, 90, 120];
const mapWidth = 1440;
const mapHeight = (10 / 16) * 1440;
const mapPaddingX = 0.04 * mapWidth;
const mapPaddingY = 0.05 * mapHeight;
const trimmedMapWidth = mapWidth - 2 * mapPaddingX;
const trimmedMapHeight = mapHeight - 2 * mapPaddingY;
const halfTrimmedMapWidth = trimmedMapWidth / 2;
const halfTrimmedMapHeight = trimmedMapHeight / 2;
const fieldDepth = 105;
const fieldWidth = 68;
const flightTimeScale = 500;
const radialSize = 200;
const maxUndos = 30;
const timelineHeight = 120;
const navigationHeight = 65;
const playerSpeedLimit = 20;
const ballSpeedLimit = 40;
const goalZones: Point[] = [
  { x: 38, y: mapHeight / 2 },
  { x: mapWidth - 38, y: mapHeight / 2 },
];

export const defaultFieldSpace : FieldSpace = {
  mapWidth,
  mapHeight,
  mapPaddingX,
  mapPaddingY,
  trimmedMapWidth,
  trimmedMapHeight,
  halfTrimmedMapWidth,
  halfTrimmedMapHeight,
  fieldDepth,
  fieldWidth,
}

const initialScenarioState: ScenarioState = {
  fileName: "Untitled",
  matchTime: 0,
  endTime: 0,
  players: new Map<number, Player>(),
  ball: {
    initialPosition: { x: mapWidth / 2, y: mapHeight / 2 },
    initialPossessor: 1,
    points: [],
    segments: [],
    debugPoints: [],
  },
};

const initialScenarioClip: ScenarioClip = {
  scenario: initialScenarioState,
  undoStates: [initialScenarioState],
  redoStates: [],
  isDirty: false,
  isPreview: false,
  isStaging: false,
  id: 0,
};

export const initialState: BuilderState = {
  clip: initialScenarioClip,
  availableTeamMates: [...fieldPositions],
  availableOpponents: [...fieldPositions],
  availableIds: [...ids],
  time: 0,
  selectedPlayer: -1,
  selectedWaypoint: -1,
  selectedPosition: { x: 0, y: 0 },
  selectedAction: -1,
  showPlayerRadial: false,
  showFieldRadial: false,
  showWaypointRadial: false,
  showActionRadial: false,
  showWarnings: false,
  showBuilderMenu: false,
  radialAnchor: { x: 0, y: 0 },
  viewWidth: window.innerWidth,
  radialSize: radialSize,
  viewOffset: { x: 0, y: 0 },
  viewRotation: 0,
  spriteScale: 1,
  mapScale: 1,
  field: defaultFieldSpace,
  targetMode: TargetMode.None,
  lastClickTarget: null,
  nextPossessor: -1,
  prevPossessor: -1,
  options: {
    showWaypoints: true,
    showActions: true,
    showBallPath: true,
    pathLength: 4,
    maxUndos: maxUndos,
    showJerseyNumbers: false,
    gameSpeed: 1,
    playerSpeedLimit: playerSpeedLimit,
    ballSpeedLimit: ballSpeedLimit,
    playbackResolution: 0.25,
  },
  copiedAction: undefined,
  timelineHeight: timelineHeight,
  navigationHeight: navigationHeight,
  warnings: [],
  ballStates: [{ state: "idle", time: 0, player: 1 }],
  ballPoints: [],
  showImportDialog: false,
  physicsPrompt: null
};

const BuilderContext = React.createContext({
  state: initialState,
  dispatch: (config: any) => {
    console.log("Using default dispatch", config);
  },
});

const isChanged = (oldValue: any, newValue: any, isPopulatedPredicate: (val: any) => boolean) => {
  // A field is considered changed if it is not defined in previous state and there is a new value
  // or if there is a new value and its different from the previous value
  const hasNewValue = isPopulatedPredicate(newValue);
  if (isPopulatedPredicate(oldValue) && hasNewValue) {
    return !isEqual(oldValue, newValue);
  }
  return hasNewValue;
};

const isDefined = (val: any) => val !== undefined;

function reducer(state: BuilderState, data: any) {
  // If the state change is due to an edit, determine if the scenario state has changed and if so add it to the undo stack
  if (!data.stateChangeSource || data.stateChangeSource === StateChangeSource.Edit) {
    const previousState = state.clip.undoStates[state.clip.undoStates.length - 1];

    // Check if the scenario state has changed
    // NOTE: if there is no previous state, then the state is considered changed
    let isAnyChanged = true;
    const scenarioData = data.clip?.scenario
      ? {
          players: data.clip.scenario.players,
          ball: data.clip.scenario.ball,
          endTime: data.clip.scenario.endTime,
          fileName: data.clip.scenario.fileName,
        }
      : data;

    if (previousState) {
      const isEndTimeChanged = isChanged(previousState.endTime, scenarioData.endTime, isNumber);
      const isFileNameChanged = isChanged(previousState.fileName, scenarioData.fileName, isString);
      const isPlayersChanged = isChanged(previousState.players, scenarioData.players, isMap);
      const isBallChanged = isChanged(previousState.ball, scenarioData.ball, isDefined);
      isAnyChanged = isEndTimeChanged || isFileNameChanged || isPlayersChanged || isBallChanged;
      // console.log('isEndTimeChanged', isEndTimeChanged, 'isFileNameChanged', isFileNameChanged, 'isPlayersChanged', isPlayersChanged, 'isBallChanged', isBallChanged);
    }

    if (isAnyChanged) {
      // push the new undo onto the undo stack
      const newUndoState = { ...merge(state.clip.undoStates[state.clip.undoStates.length - 1], scenarioData) } as ScenarioState;
      const newUndoStates = [...state.clip.undoStates];
      newUndoStates.push(newUndoState);

      // if the undo stack exceeds the maximum number of undos, remove the oldest undo
      const maxUndos = data.options && data.options.maxUndos ? data.options.maxUndos : state.options.maxUndos;
      if (newUndoStates.length > maxUndos) {
        newUndoStates.shift();
      }

      if(data.clip) { // Crash fixed when clicking ball, doesn't have clip data
        data.clip.isDirty = true;
        data.clip.undoStates = newUndoStates;
        data.clip.redoStates = []; // redo stack is cleared if a new edit is made
      }
    }
  }

  if (data.stateChangeSource === StateChangeSource.Load) {
    data.clip.undoStates = [
      {
        players: data.players,
        ball: data.ball,
        endTime: data.endTime,
        fileName: data.fileName,
      },
    ];
    data.clip.redoStates = [];
    data.clip.isDirty = !!data.forceDirty;
  }

  //@ts-ignore
  state = { ...merge(state, data) };

  return state;
}

const useBuilderContext = () => React.useContext(BuilderContext);

const ContextBridge = function ContextBridge<T>(props: {
  children: React.ReactNode;
  Context: React.Context<T>;
  render: (children: React.ReactNode) => JSX.Element;
}): JSX.Element {
  const Context = props.Context;
  const result = <Context.Consumer>{(value) => props.render(<Context.Provider value={value}>{props.children}</Context.Provider>)}</Context.Consumer>;
  return result;
};

const Stage = ({ children, ...props }: any) => {
  return (
    <ContextBridge Context={BuilderContext} render={(children) => <PixiStage {...props}>{children}</PixiStage>}>
      {children}
    </ContextBridge>
  );
};

const BuilderContextProvider = (props: any) => {
  const overrideState = props.overrides ? { ...initialState, ...props.overrides } : initialState;
  const [reducerState, setReducerState] = React.useReducer(reducer, overrideState);
  const value = { state: reducerState, dispatch: setReducerState };

  return (
    //@ts-ignore
    <BuilderContext.Provider value={value}>{props.children}</BuilderContext.Provider>
  );
};

export {
  BuilderContext,
  BuilderContextProvider,
  fieldDepth,
  fieldWidth,
  flightTimeScale,
  goalZones,
  kickHeights,
  largeScoreTimes,
  pressureColor,
  scores,
  scoreTimes,
  Stage,
  teamAColor,
  teamBColor,
  useBuilderContext,
};

